import {
  Box,
  FormControlLabel,
  Checkbox,
  useTheme,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import { motion } from "framer-motion";
import { Icon } from "@iconify/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

type UploadSectionProps = {
  label: string;
  image: File | null;
  open: boolean;
  setOpen: (v: boolean) => void;
  inputId: string;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onCheckboxClick: () => void;
  delay?: number;
};

export default function UploadSection({
  label,
  image,
  open,
  setOpen,
  inputId,
  onFileChange,
  onCheckboxClick,
  delay = 0,
}: UploadSectionProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const [showOptions, setShowOptions] = useState(false);

  const handleCameraClick = () => {
    const input = document.getElementById(
      `${inputId}-camera`
    ) as HTMLInputElement;
    input?.click();
    setShowOptions(false);
  };

  const handleGalleryClick = () => {
    const input = document.getElementById(
      `${inputId}-gallery`
    ) as HTMLInputElement;
    input?.click();
    setShowOptions(false);
  };

  const handleImageAreaClick = () => {
    setShowOptions(true);
  };
  return (
    <Box mb={6} width="80%">
      <FormControlLabel
        control={
          <Checkbox
            checked={!!image}
            icon={
              <Icon
                icon="akar-icons:circle"
                width="16"
                height="16"
                style={{ color: theme.palette.primary.dark }}
              />
            }
            checkedIcon={
              <Icon
                icon="akar-icons:circle-check-fill"
                width="16"
                height="16"
                style={{ color: theme.palette.primary.dark }}
              />
            }
            onChange={onCheckboxClick}
          />
        }
        label={
          <Box
            display="flex"
            alignItems="center"
            sx={{ cursor: "pointer" }}
            onClick={() => setOpen(!open)}
          >
            <span
              style={{
                fontSize: "1rem",
                fontWeight: "500",
                color: "#3D3A6A",
              }}
            >
              {label}
            </span>
            {open ? (
              <KeyboardArrowDownIcon sx={{ ml: 1 }} />
            ) : (
              <KeyboardArrowLeftIcon sx={{ ml: 1 }} />
            )}
          </Box>
        }
      />
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: open ? 1 : 0, y: open ? 0 : 40 }}
        transition={{ duration: 0.4, delay }}
        style={{
          overflow: "hidden",
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {open && (
          <>
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              border="2px solid #E5A39C"
              borderRadius={3}
              height={150}
              width="250px"
              sx={{ cursor: "pointer" }}
              onClick={handleImageAreaClick}
            >
              {image ? (
                <img
                  src={URL.createObjectURL(image)}
                  alt={inputId}
                  style={{ maxHeight: "100%", maxWidth: "100%" }}
                />
              ) : (
                <img
                  src="/images/icons/camera.webp"
                  alt="Upload Icon"
                  style={{ height: 80 }}
                />
              )}
            </Box>

            {/* Hidden inputs for camera and gallery */}
            <input
              id={`${inputId}-camera`}
              type="file"
              accept="image/*"
              capture="environment"
              hidden
              onChange={onFileChange}
            />
            <input
              id={`${inputId}-gallery`}
              type="file"
              accept="image/*"
              hidden
              onChange={onFileChange}
            />

            {/* Options Dialog */}
            <Dialog open={showOptions} onClose={() => setShowOptions(false)}>
              <DialogTitle sx={{ color: "#3D3A6A" }}>
                {t("uploadSection.selectMethod", "เลือกวิธีการอัปโหลดรูปภาพ")}
              </DialogTitle>
              <DialogContent>
                <Box display="flex" flexDirection="column" gap={2} pt={1}>
                  <Button
                    variant="outlined"
                    startIcon={<CameraAltIcon />}
                    onClick={handleCameraClick}
                    fullWidth
                    sx={{
                      justifyContent: "flex-start",
                      py: 2,
                      color: "#3D3A6A",
                      borderColor: "#E5A39C",
                      "&:hover": {
                        backgroundColor: "#ffeaa7",
                        borderColor: "#d19a8c",
                      },
                    }}
                  >
                    {t("uploadSection.takePhoto", "ถ่ายรูปด้วยกล้อง")}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<PhotoLibraryIcon />}
                    onClick={handleGalleryClick}
                    fullWidth
                    sx={{
                      justifyContent: "flex-start",
                      py: 2,
                      color: "#3D3A6A",
                      borderColor: "#E5A39C",
                      "&:hover": {
                        backgroundColor: "#ffeaa7",
                        borderColor: "#d19a8c",
                      },
                    }}
                  >
                    {t("uploadSection.selectFromGallery", "เลือกจากแกลเลอรี่")}
                  </Button>
                </Box>
              </DialogContent>
              <DialogActions>
                <Button
                  variant="outlined"
                  sx={{
                    color: "#3D3A6A",
                    borderColor: "#3D3A6A",
                  }}
                  onClick={() => setShowOptions(false)}
                >
                  {t("common.cancel", "ยกเลิก")}
                </Button>
              </DialogActions>
            </Dialog>
          </>
        )}
      </motion.div>
    </Box>
  );
}
